import { Col, Row } from "react-bootstrap";
import { useState, useRef, useEffect } from "react";
import TableSchemaBuilder from "./TableSchemaBuilder";
import MultiSelectDropdown from "../../Utilities/MultiSelectDropdown";
import KeywordsManager from "./KeyWordsManager";
import SingleSelectDropdown from "../../GlobalComponents/FormComponents/Dropdowns/SingleSelectDropdown";
import Select from "react-select";

const KPI_TYPE_OPTIONS = [
  { value: "EMISSION", label: "Emission", icon: "🌍" },
  { value: "ENERGY", label: "Energy", icon: "⚡" },
  { value: "NOx", label: "NOx", icon: "💨" },
  { value: "SOx", label: "SOx", icon: "🌫️" },
  { value: "PPM", label: "PPM", icon: "🔬" },
];


// KPI_TYPE_OPTIONS mapped to the shape MultiSelectDropdown expects
const KPI_TYPE_MULTISELECT_OPTIONS = KPI_TYPE_OPTIONS.map(o => ({ value: o.value, label: `${o.icon} ${o.label}` }));


// ── VariablesManager ──────────────────────────────────────────────────────────
// Each formula variable (e.g. "Q594") may optionally be converted to a target
// unit before the formula is evaluated. The target unit is picked from the
// global unit master and stored in `variable_units_json` as { variable: unitId }.
const VariablesManager = ({ variables, variableUnits, unitMaster = [], index, handleChange }) => {
  const [newVariable, setNewVariable] = useState("");
  const variablesList = typeof variables === "string" ? (variables ? JSON.parse(variables) : []) : (variables || []);
  const unitsMap = typeof variableUnits === "string" ? (variableUnits ? JSON.parse(variableUnits) : {}) : (variableUnits || {});

  const unitOptions = unitMaster.map(um => ({ value: um.id, label: `${um.unit_name} (${um.unit})` }));
  const getUnitOption = (v) => {
    const id = unitsMap[v];
    if (id === undefined || id === null || id === "") return null;
    return unitOptions.find(opt => String(opt.value) === String(id)) || null;
  };

  const handleAddVariable = () => { if (newVariable.trim()) { handleChange({ target: { name: "variables_json", value: [...variablesList, newVariable.trim()] } }, index); setNewVariable(""); } };
  const handleRemoveVariable = i => {
    const removed = variablesList[i];
    handleChange({ target: { name: "variables_json", value: variablesList.filter((_, idx) => idx !== i) } }, index);
    if (removed in unitsMap) {
      const next = { ...unitsMap };
      delete next[removed];
      handleChange({ target: { name: "variable_units_json", value: next } }, index);
    }
  };
  const handleUnitChange = (v, unitId) => {
    const next = { ...unitsMap };
    if (unitId === null || unitId === undefined || unitId === "") delete next[v];
    else next[v] = unitId;
    handleChange({ target: { name: "variable_units_json", value: next } }, index);
  };

  return (
    <div className="mb-3">
      <label className="form-label fw-semibold text-dark"><i className="fas fa-code text-primary me-2"></i>Variables*</label>
      <div className="input-group mb-2">
        <input type="text" className="form-control font-monospace" placeholder="e.g., Q594" value={newVariable} onChange={e => setNewVariable(e.target.value)} onKeyPress={e => { if (e.key === "Enter") { e.preventDefault(); handleAddVariable(); } }} />
        <button type="button" className="btn btn-primary" onClick={handleAddVariable} disabled={!newVariable.trim()}><i className="fas fa-plus me-1"></i> Add</button>
      </div>
      {variablesList.length > 0 ? (
        <div className="d-flex flex-column gap-2 p-3 bg-light rounded">
          {variablesList.map((v, idx) => (
            <div key={idx} className="d-flex align-items-center gap-2 flex-wrap">
              <span className="badge bg-primary d-flex align-items-center gap-2" style={{ fontSize: "0.9rem", padding: "0.5rem 0.75rem" }}>
                <span className="font-monospace">{v}</span>
                <button type="button" className="btn-close btn-close-white" style={{ fontSize: "0.6rem" }} onClick={() => handleRemoveVariable(idx)} aria-label="Remove" />
              </span>
              <span className="text-muted small">convert to</span>
              <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                <Select
                  options={unitOptions}
                  value={getUnitOption(v)}
                  onChange={opt => handleUnitChange(v, opt ? opt.value : "")}
                  placeholder="No conversion"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : <div className="text-muted small p-2 bg-light rounded">No variables added yet.</div>}
    </div>
  );
};

// Add this component alongside VariablesManager
const EvidencesManager = ({ evidences = [], index, handleChange }) => {
  const [newEvidence, setNewEvidence] = useState("");

  const handleAdd = () => {
    if (newEvidence.trim()) {
      handleChange({ target: { name: "required_evidences", value: [...evidences, newEvidence.trim()] } }, index);
      setNewEvidence("");
    }
  };

  const handleRemove = i =>
    handleChange({ target: { name: "required_evidences", value: evidences.filter((_, idx) => idx !== i) } }, index);

  return (
    <div className="mb-3">
      <label className="form-label fw-semibold text-dark">
        <i className="fas fa-paperclip text-primary me-2"></i>Required Evidences*
      </label>
      <div className="input-group mb-2">
        <input
          type="text"
          className="form-control"
          placeholder="e.g., Annual report, Audit certificate"
          value={newEvidence}
          onChange={e => setNewEvidence(e.target.value)}
          onKeyPress={e => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }}
        />
        <button type="button" className="btn btn-primary" onClick={handleAdd} disabled={!newEvidence.trim()}>
          <i className="fas fa-plus me-1"></i> Add
        </button>
      </div>
      {evidences.length > 0 ? (
        <div className="d-flex flex-wrap gap-2 p-3 bg-light rounded">
          {evidences.map((ev, idx) => (
            <span key={idx} className="badge bg-secondary d-flex align-items-center gap-2" style={{ fontSize: "0.9rem", padding: "0.5rem 0.75rem" }}>
              <span>{ev}</span>
              <button type="button" className="btn-close btn-close-white" style={{ fontSize: "0.6rem" }} onClick={() => handleRemove(idx)} aria-label="Remove" />
            </span>
          ))}
        </div>
      ) : (
        <div className="text-muted small p-2 bg-light rounded">No evidences added yet.</div>
      )}
    </div>
  );
};

const SupportedUnitsManager = ({ supportedUnits = [], unitMaster = [], index, onChange }) => {
  const handleAddUnit = () => {
    const isBase = supportedUnits.length === 0;
    onChange([...supportedUnits, { unit_id: "", is_base: isBase, factor: isBase ? 1 : "" }], index);
  };

  const handleRemoveUnit = (i) => {
    let updated = [...supportedUnits];
    const wasBase = updated[i].is_base;
    updated.splice(i, 1);
    if (wasBase && updated.length > 0) {
      updated[0].is_base = true;
      updated[0].factor = 1;
    }
    onChange(updated, index);
  };

  const handleUnitChange = (i, field, value) => {
    const updated = supportedUnits.map(u => ({ ...u }));
    if (field === "is_base" && value === true) {
      updated.forEach((u, idx) => { u.is_base = idx === i; });
      updated[i].factor = 1;
    } else {
      updated[i][field] = field === "factor" ? (value === "" ? "" : Number(value)) : value;
      if (updated[i].is_base) updated[i].factor = 1;
    }
    onChange(updated, index);
  };

  const unitOptions = unitMaster.map(um => ({
    value: um.id,
    label: `${um.unit_name} (${um.unit})`
  }));

  const getOptionValue = (unit_id) => {
    if (!unit_id && unit_id !== 0) return null;
    const found = unitOptions.find(opt => String(opt.value) === String(unit_id));
    return found || { value: unit_id, label: String(unit_id) };
  };

  return (
    <div className="mb-3">
      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label fw-semibold text-dark mb-0">
          <i className="fas fa-ruler-combined text-primary me-2"></i>
          Supported Units
          <span className="ms-2 badge bg-secondary" style={{ fontSize: "0.7rem", fontWeight: 500 }}>Optional</span>
        </label>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleAddUnit}
          style={{ borderRadius: "6px", fontSize: "0.8rem" }}
        >
          <i className="fas fa-plus me-1"></i> Add Unit
        </button>
      </div>

      {supportedUnits.length === 0 ? (
        <div
          className="text-center py-3 px-3 rounded border"
          style={{ borderStyle: "dashed", borderColor: "#cbd5e1", background: "#f8fafc", color: "#94a3b8", fontSize: "0.85rem" }}
        >
          <i className="fas fa-balance-scale me-2 opacity-50"></i>
          No units configured. Click <strong>Add Unit</strong> to get started.
        </div>
      ) : (
        <div className="border rounded overflow-hidden" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          {/* Table header */}
          <div
            className="d-flex align-items-center px-3 py-2"
            style={{ background: "#f1f5f9", borderBottom: "1px solid #e2e8f0", fontSize: "0.78rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}
          >
            <div style={{ flex: "0 0 40%" }}>Unit</div>
            <div style={{ flex: "0 0 20%", textAlign: "center" }}>Base</div>
            <div style={{ flex: "0 0 28%" }}>Factor</div>
            <div style={{ flex: "0 0 12%", textAlign: "center" }}>Del</div>
          </div>

          {/* Rows */}
          {supportedUnits.map((u, i) => (
            <div
              key={i}
              className="d-flex align-items-center px-3 py-2"
              style={{
                borderBottom: i < supportedUnits.length - 1 ? "1px solid #f1f5f9" : "none",
                background: u.is_base ? "#f0fdf4" : "#fff",
                transition: "background 0.15s",
              }}
            >
              {/* Unit dropdown */}
              <div style={{ flex: "0 0 40%", paddingRight: "8px" }}>
                <SingleSelectDropdown
                  options={unitOptions}
                  value={getOptionValue(u.unit_id)}
                  onChange={(opt) => handleUnitChange(i, "unit_id", opt ? opt.value : "")}
                  placeholder="Search or type to create..."
                />
              </div>

              {/* Base radio */}
              <div style={{ flex: "0 0 20%", textAlign: "center" }}>
                <div className="d-flex flex-column align-items-center">
                  <input
                    type="radio"
                    name={`is_base_${index}`}
                    id={`is_base_${index}_${i}`}
                    checked={!!u.is_base}
                    onChange={(e) => handleUnitChange(i, "is_base", e.target.checked)}
                    style={{ cursor: "pointer", width: "16px", height: "16px", accentColor: "#22c55e" }}
                  />
                  {u.is_base && (
                    <span style={{ fontSize: "0.65rem", color: "#16a34a", fontWeight: 700, marginTop: "2px" }}>BASE</span>
                  )}
                </div>
              </div>

              {/* Factor */}
              <div style={{ flex: "0 0 28%", paddingRight: "8px" }}>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder={u.is_base ? "Auto: 1" : "e.g. 1000"}
                  value={u.factor}
                  disabled={u.is_base}
                  onChange={(e) => handleUnitChange(i, "factor", e.target.value)}
                  style={{ background: u.is_base ? "#f0fdf4" : "#fff", borderColor: "#e2e8f0", fontSize: "0.85rem" }}
                />
              </div>

              {/* Delete */}
              <div style={{ flex: "0 0 12%", textAlign: "center" }}>
                <button
                  type="button"
                  className="btn btn-link text-danger p-0"
                  onClick={() => handleRemoveUnit(i)}
                  title="Remove"
                  style={{ fontSize: "0.95rem" }}
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ── Main QuestionFormField ────────────────────────────────────────────────────
const QuestionFormField = ({
  item,
  index,
  dropdownItems,
  unitSettings = [],
  unitMaster = [],
  allFrameworks,
  allIndustries,
  handleChange,
  handleIndustryChange,
  handleFrameworkChange,
  handleUnitCategoryChange,
  handleKPiStatusChange,
  handleKPITypeChange,
  handleIsFormulaBased,
  handleQuestionDetailLabelChange,
  handleTabularSchemaChange,
  handleRemove,
  handleSupportedUnitsChange,
  inputFieldsLength,
}) => {

  const questionTypes = ["qualitative", "yes_no", "quantitative", "tabular_question", "quantitative_trends"];
  const categoryOptions = [{ value: "E", label: "Essential" }, { value: "L", label: "Leadership" }, { value: "G", label: "General" }];
  const industryOptions = allIndustries.map(i => ({ value: i.id, label: `${i.name} (${i.industry_type})` }));
  const frameworkOptions = allFrameworks.map(fw => ({ value: fw.id, label: fw.title }));

  const [tableSchema, setTableSchema] = useState();
  const dropdownRef = useRef(null);
  const isInitialMount = useRef(true);
  const lastSchemaRef = useRef(null);

  useEffect(() => {
    const fn = e => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) { } };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    if (tableSchema && JSON.stringify(tableSchema) !== JSON.stringify(lastSchemaRef.current)) {
      lastSchemaRef.current = tableSchema;
      handleTabularSchemaChange(tableSchema, index);
    }
  }, [tableSchema]);

  const kpiTypeArray = Array.isArray(item.kpi_type) ? item.kpi_type : [];

  const renderSupportedUnits = () => {
    if (!["quantitative", "quantitative_trends"].includes(item.question_type)) return null;
    
    // Normalise: could be null/undefined (old questions), a string (raw JSON), or an array
    let units = item.supported_units;
    if (!units) units = [];
    else if (typeof units === "string") { try { units = JSON.parse(units); } catch { units = []; } }
    else if (!Array.isArray(units)) units = [];

    return (
      <Col md={12}>
        <SupportedUnitsManager 
          supportedUnits={units}
          unitMaster={unitMaster}
          index={index}
          onChange={handleSupportedUnitsChange}
        />
      </Col>
    );
  };

  const renderAggregationMethod = () => {
    if (item.question_type !== "quantitative") return null;

    return (
      <Col md={12}>
        <div className="mb-3">
          <label className="form-label fw-semibold text-dark"><i className="fas fa-layer-group text-primary me-2"></i> Aggregation Method*</label>
          <select className="form-select" name="aggregation_method" value={item.aggregation_method || "SUM"} onChange={e => handleChange(e, index)}>
            <option value="SUM">SUM</option>
            <option value="LATEST">LATEST</option>
            <option value="AVG">AVG</option>
          </select>
          <div className="mt-1" style={{ fontSize: "0.78rem", color: "#9ca3af" }}>How multiple months are combined on dashboards — e.g. Grid Electricity = Sum, Total Built-up Area = Latest, Employee Attrition Rate = Avg.</div>
        </div>
      </Col>
    );
  };

  const renderQuestionTypeSpecificFields = () => {
    switch (item.question_type) {
      case "tabular_question":
        return (
          <div className="mt-3">
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <label className="fw-semibold text-dark mb-0 d-flex align-items-center" htmlFor={`isReadOnly-${index}`}>
                  <i className="fas fa-lock text-primary me-2"></i> Is Read Only
                </label>
                <input className="form-check-input" type="checkbox" id={`isReadOnly-${index}`}
                  checked={!!(item.is_read_only === 1)}
                  onChange={e => handleChange({ target: { name: "is_read_only", value: e.target.checked ? 1 : null } }, index)}
                />
              </div>
            </div>
            <TableSchemaBuilder tableSchemaForEdit={item.question_details} setTableSchema={setTableSchema} unitSettings={unitSettings} />
          </div>
        );
      case "yes_no":
        return <div className="mt-3"><TableSchemaBuilder tableSchemaForEdit={item.question_details} setTableSchema={setTableSchema} unitSettings={unitSettings} restrictToYesNo={true} /></div>;
      case "quantitative":
      case "quantitative_trends":
        return (
          <div className="mt-3">
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <label className="fw-semibold text-dark mb-0 d-flex align-items-center" htmlFor={`isFormulaBased-${index}`}>
                  <i className="fas fa-calculator text-primary me-2"></i> Is Formula Based
                </label>
                <input className="form-check-input" type="checkbox" id={`isFormulaBased-${index}`}
                  checked={!!(item.is_formula_based === 1)}
                  onChange={e => handleIsFormulaBased({ target: { name: "is_formula_based", value: e.target.checked ? 1 : 0 } }, index)}
                />
              </div>
            </div>
            {item.is_formula_based === 1 && (
              <>
                <div className="mb-3">
                  <label className="form-label fw-semibold text-dark"><i className="fas fa-function text-primary me-2"></i> Formula*</label>
                  <textarea name="formula" className="form-control font-monospace" placeholder="e.g., {{Q594}}+{{Q595}}" value={item.formula || ""} onChange={e => handleChange(e, index)} rows={2} />
                </div>
                <VariablesManager variables={item.variables_json || []} variableUnits={item.variable_units_json || {}} unitMaster={unitMaster} index={index} handleChange={handleChange} />
              </>
            )}
          </div>
        );
      default: return null;
    }
  };

  return (
    <>
      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:.4; } }
      `}</style>

      <div className="Questions__Boxes_forEntity p-4 mb-4 border rounded shadow-sm" style={{ backgroundColor: "#ffffff" }}>
        <Row>

          {/* ── Header ── */}
          <Col md={12}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="text-primary mb-0" style={{ fontWeight: 600 }}><i className="fas fa-question-circle me-2"></i>Question {index + 1}</h5>
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-semibold text-muted" style={{ fontSize: 13 }}>Status</span>
                  <div className="form-check form-switch m-0">
                    <input className="form-check-input" type="checkbox" role="switch" checked={!!(item.status === 1)} onChange={e => handleChange({ target: { name: "status", value: e.target.checked ? 1 : 0 } }, index)} />
                  </div>
                </div>
                {inputFieldsLength >= 2 && (
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleRemove(index)} style={{ width: 36, height: 36, padding: 0 }}>
                    <i className="fas fa-trash"></i>
                  </button>
                )}
              </div>
            </div>
          </Col>

          {/* ── Question Type ── */}
          <Col md={12}>
            <div className="mb-3">
              <label className="form-label fw-semibold text-dark"><i className="fas fa-cog text-primary me-2"></i> Select Question Type*</label>
              <select className="form-select" name="question_type" value={item.question_type} onChange={e => handleChange(e, index)}>
                <option value="">Select Question Type</option>
                <option value="qualitative">📝 Qualitative</option>
                <option value="yes_no">✅ Yes/No</option>
                <option value="quantitative">🔢 Quantitative</option>
                <option value="tabular_question">📊 Tabular Question</option>
                <option value="quantitative_trends">📈 Quantitative Trends</option>
              </select>
            </div>
          </Col>

          {questionTypes.includes(item.question_type) && (
            <>
              <Col md={12}>
                <div className="mb-3">
                  <label className="form-label fw-semibold text-dark"><i className="fas fa-heading text-primary me-2"></i> Question Title*</label>
                  <textarea name="question_title" className="form-control" value={item.question_title} onChange={e => handleChange(e, index)} rows={3} />
                </div>
              </Col>
              <Col md={12}>
                <div className="mb-3">
                  <label className="form-label fw-semibold text-dark"><i className="fas fa-align-left text-primary me-2"></i> Default Description (Every financial year)</label>
                  <textarea name="default_description" className="form-control" value={item.default_description || ""} onChange={e => handleChange(e, index)} rows={3} placeholder="Shown for every financial year unless overridden below" />
                </div>
              </Col>
              <Col md={12}>
                <div className="mb-3">
                  <label className="form-label fw-semibold text-dark"><i className="fas fa-align-left text-primary me-2"></i> This Year Description</label>
                  <textarea name="year_description" className="form-control" value={item.year_description || ""} onChange={e => handleChange(e, index)} rows={3} placeholder="Overrides the default for the selected financial year only; leave blank to use the default" />
                </div>
              </Col>
              <Col md={12}>
                <div className="mb-3">
                  <label className="form-label fw-semibold text-dark"><i className="fas fa-clock text-primary me-2"></i> Frequency*</label>
                  <select className="form-select" name="frequency" value={item.frequency || ""} onChange={e => handleChange(e, index)}>
                    <option value="">Select Frequency</option>
                    {dropdownItems.map((d, i) => <option key={i} value={d.value}>{d.display}</option>)}
                  </select>
                </div>
              </Col>
              <Col md={12}>
                <div className="mb-3">
                  <label className="form-label fw-semibold text-dark"><i className="fas fa-bookmark text-primary me-2"></i> Category*</label>
                  <select className="form-select" name="category" value={item.category || ""} onChange={e => handleChange(e, index)}>
                    <option value="">Select Category</option>
                    {categoryOptions.map((o, i) => <option key={i} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </Col>

              <Col md={12}>
                <div className="mb-3">
                  <MultiSelectDropdown
                    label="KPI Type"
                    icon="fas fa-bolt"
                    placeholder="Select KPI Type(s)"
                    options={KPI_TYPE_MULTISELECT_OPTIONS}
                    selectedValues={kpiTypeArray}
                    onChange={vals => handleKPITypeChange(vals, index)}
                  />
                  <div className="mt-1" style={{ fontSize: "0.78rem", color: "#9ca3af" }}>Stored as JSON array. Select none to save as NULL.</div>
                </div>
              </Col>

            
              <Col md={12} className="mt-3">
                <div className="mb-3">
                  <label className="form-label fw-semibold text-dark">
                    <i className="fas fa-tags text-primary me-2"></i>Keywords
                  </label>
                  <KeywordsManager
                    tags={item.tags}
                    index={index}
                    handleChange={handleChange}
                  />
                </div>
              </Col>

              {/* ── Checkboxes ── */}
              <Col md={12}><div className="my-2 d-flex justify-content-between align-items-center"><label className="fw-semibold text-dark mb-0"><i className="fas fa-check-circle text-primary me-2"></i> Applicable Check Required*</label><input className="form-check-input" type="checkbox" checked={!!(item.applicable_check === 1)} onChange={e => handleChange({ target: { name: "applicable_check", value: e.target.checked ? 1 : 0 } }, index)} /></div></Col>
              <Col md={12}><div className="my-2 d-flex justify-content-between align-items-center"><label className="fw-semibold text-dark mb-0"><i className="fas fa-check-circle text-primary me-2"></i> Applicable Note Required*</label><input className="form-check-input" type="checkbox" checked={!!(item.applicable_note === 1)} onChange={e => handleChange({ target: { name: "applicable_note", value: e.target.checked ? 1 : 0 } }, index)} /></div></Col>
              <Col md={12}><div className="my-2 d-flex justify-content-between align-items-center"><label className="fw-semibold text-dark mb-0"><i className="fas fa-check-circle text-primary me-2"></i> Triger Check Required*</label><input className="form-check-input" type="checkbox" checked={!!(item.triger_check === 1)} onChange={e => handleChange({ target: { name: "triger_check", value: e.target.checked ? 1 : 0 } }, index)} /></div></Col>

              {renderSupportedUnits()}
              {renderAggregationMethod()}

              <Col md={12}><MultiSelectDropdown label="Industries" icon="fas fa-layer-group" options={industryOptions} selectedValues={item.industry_ids || []} onChange={ids => handleIndustryChange(ids, index)} /></Col>
              <Col md={12}><MultiSelectDropdown label="Frameworks" icon="fas fa-layer-group" options={frameworkOptions} selectedValues={item.map_framework_ids || []} onChange={ids => handleFrameworkChange(ids, index)} /></Col>
            </>
          )}

          <Col md={12}>
            <EvidencesManager evidences={item.required_evidences || []} index={index} handleChange={handleChange} />
          </Col>

          <Col md={12}>
            <div className="mb-3">
              <label className="form-label fw-semibold text-dark"><i className="fas fa-heading text-primary me-2"></i> Framework Section Mapping*</label>
              <textarea name="framework_section_mapping" className="form-control" value={item.framework_section_mapping} onChange={e => handleChange(e, index)} rows={3} />
            </div>
          </Col>

          <Col md={12}>{renderQuestionTypeSpecificFields()}</Col>

          <Col md={12}>
            <div className="d-flex align-items-center gap-3">
              <label className="fw-semibold text-dark mb-0"><i className="fas fa-upload text-primary me-2"></i>KPI Status</label>
              <select className="form-select form-select-sm" name="kpi_status" value={item.kpi_status || "DRAFT"} onChange={e => handleKPiStatusChange(e, index)} style={{ width: 150 }}>
                <option value="DRAFT">📝 Draft</option>
                <option value="PUBLISHED">🚀 Published</option>
              </select>
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default QuestionFormField;